if (process.env.VUE_APP_DEBUG !== "local") {
  console.stdlog = console.log.bind(console);
  console.logs = [];
  console.log = function () {
    console.logs.push(["LOG", new Date(), ...Array.from(arguments)]);
    //console.stdlog.apply(console, arguments);
  };

  console.stdError = console.error.bind(console);
  console.error = function () {
    console.logs.push(["ERROR", new Date(), ...Array.from(arguments)]);
    //console.stdError.apply(console, arguments);
  };

  console.stdInfo = console.info.bind(console);
  console.info = function () {
    console.logs.push(["INFO", new Date(), ...Array.from(arguments)]);
    //console.stdInfo.apply(console, arguments);
  };

  console.stdTrace = console.trace.bind(console);
  console.trace = function () {
    console.logs.push(["TRACE", new Date(), ...Array.from(arguments)]);
    //console.stdTrace.apply(console, arguments);
  };

  console.stdWarn = console.warn.bind(console);
  console.warn = function () {
    console.logs.push(["WARN", new Date(), ...Array.from(arguments)]);
    //console.stdWarn.apply(console, arguments);
  };
}

import Vue, { createApp } from "vue";
import App from "./App.vue";
import { createProvider } from "./vue-apollo";
import router from "./router";

import axios from "axios";
import VueAxios from "vue-axios";
Vue.use(VueAxios, axios);
const baseURL = process.env.VUE_APP_API_URL || "http://localhost:8000";
Vue.axios.defaults.baseURL = baseURL;

Vue.config.productionTip = false;

// bootstrap
import { BootstrapVue, BootstrapVueIcons } from "bootstrap-vue";
import "bootstrap/dist/css/bootstrap.css";
import "bootstrap-vue/dist/bootstrap-vue.css";
Vue.use(BootstrapVue);
Vue.use(BootstrapVueIcons);
import BFormCheckbox from "@/components/compat/BFormCheckbox.vue";
Vue.component("BFormCheckbox", BFormCheckbox);

// Scrollbar
import simplebar from "@/components/ui/SimpleBar.vue";
Vue.component("simplebar", simplebar);

import VCalendar from "v-calendar";
import "v-calendar/style.css";

import { defineRule, configure } from "vee-validate";
import * as rules from "@vee-validate/rules";
import { localize } from "@vee-validate/i18n";
import de from "@vee-validate/i18n/dist/locale/de.json";

const skipRules = new Set(["all", "toTypedSchema"]);
for (const [name, rule] of Object.entries(rules)) {
  if (!skipRules.has(name) && typeof rule === "function") {
    defineRule(name, rule);
  }
}

configure({
  generateMessage: localize("de", de),
});

import "@/helpers/validationRules";
import ValidationProvider from "@/components/validation/ValidationProvider.vue";
import ValidationObserver from "@/components/validation/ValidationObserver.vue";
Vue.component("ValidationProvider", ValidationProvider);
Vue.component("ValidationObserver", ValidationObserver);
import UplImage from "@/components/ui/UplImage";
Vue.component("UplImage", UplImage);

// custom styling
import "./assets/scss/app.scss";

// font awesome
import "@/plugins/fontawesome/fontawesome.js";

import titleMixin from "./mixins/title";
Vue.mixin(titleMixin);
import formatDateTimeMixin from "./mixins/formatDateTime";
Vue.mixin(formatDateTimeMixin);
import formatDateMixin from "./mixins/formatDate";
Vue.mixin(formatDateMixin);
import toastMixin from "./mixins/toast";
Vue.mixin(toastMixin);
import toastMixinFail from "./mixins/toastFail";
Vue.mixin(toastMixinFail);
import toastMixinWarning from "./mixins/toastWarning";
Vue.mixin(toastMixinWarning);
import linkGen from "./mixins/linkGen";
Vue.mixin(linkGen);
import shortenType from "./mixins/shortenType";
Vue.mixin(shortenType);
import currentDate from "./mixins/currentDate";
Vue.mixin(currentDate);
import logoutMixin from "./mixins/logoutMixin";
Vue.mixin(logoutMixin);
import validateFiles from "./mixins/validateFiles";
Vue.mixin(validateFiles);
import currentDateTime from "./mixins/currentDateTime";
Vue.mixin(currentDateTime);
import translateEventType from "./mixins/translateEventType";
import PermissionManager from "./plugins/permissions/PermissionManager";
import GlobalEvents from "./plugins/GlobalEvents/GlobalEvents";
Vue.mixin(translateEventType);
import generateShortLink from "./mixins/generateShortLink";
Vue.mixin(generateShortLink);
import fileUrlBase from "./mixins/fileUrlBase";
Vue.mixin(fileUrlBase);

const apolloProvider = createProvider();

const app = createApp(App);

const isVueCompatPatchError = (err) =>
  err &&
  typeof err.message === "string" &&
  (err.message.includes("reading 'subTree'") ||
    err.message.includes("reading 'parentNode'"));

app.config.errorHandler = (err, instance, info) => {
  if (isVueCompatPatchError(err)) {
    return;
  }
  if (typeof console !== "undefined" && console.error) {
    console.error(err, instance, info);
  }
};

app.use(router);
app.use(apolloProvider);
app.config.globalProperties.$apolloProvider = apolloProvider;
app.use(VueAxios, axios);
app.use(BootstrapVue);
app.use(BootstrapVueIcons);
app.component("simplebar", simplebar);
app.component("ValidationProvider", ValidationProvider);
app.component("ValidationObserver", ValidationObserver);
app.component("UplImage", UplImage);
app.use(VCalendar);
app.use(PermissionManager, { apolloProvider, router });
app.use(GlobalEvents);
app.mixin(titleMixin);
app.mixin(formatDateTimeMixin);
app.mixin(formatDateMixin);
app.mixin(toastMixin);
app.mixin(toastMixinFail);
app.mixin(toastMixinWarning);
app.mixin(linkGen);
app.mixin(shortenType);
app.mixin(currentDate);
app.mixin(logoutMixin);
app.mixin(validateFiles);
app.mixin(currentDateTime);
app.mixin(translateEventType);
app.mixin(generateShortLink);
app.mixin(fileUrlBase);
app.mount("#app");

const externalLinkSelector = `a[href]:not(.external-no-icon):not([href^="/"]):not([href^="https://dsag.kesslerdigital.cloud"]):not([href^="mailto:"]):not([href^="tel:"]):not([href^="https://dsag-files.kesslerdigital.cloud"]):not([href^="#"]):not([href^="javascript"]):not([href^="https://dsag.de"]):not([href^="https://impulsant-dsag.de"]):not([href^="https://dsagnet.de"]):not([href^="https://files.dsagnet.de"])`;

function observeLink(mutationList) {
  for (const mutation of mutationList) {
    if (mutation.type === "attributes" && mutation.attributeName === "href") {
      if (
        mutation.target.nodeType === Node.ELEMENT_NODE &&
        mutation.target.matches(externalLinkSelector)
      ) {
        mutation.target.target = "_blank";
      }
    }
  }
}
const singleLinkObserver = new MutationObserver(observeLink);

function elementAddedCallback(mutationList) {
  for (const mutation of mutationList) {
    if (!mutation.addedNodes) {
      return;
    }
    for (const n of mutation.addedNodes) {
      if (n.nodeType === Node.ELEMENT_NODE) {
        if (n.matches(externalLinkSelector)) {
          n.target = "_blank";
        } else {
          for (const link of n.querySelectorAll("a")) {
            if (link.matches(externalLinkSelector)) {
              link.target = "_blank";
            } else {
              singleLinkObserver.observe(link, {
                attributeFilter: ["href"],
              });
            }
          }
        }
      }
    }
  }
}

const appRoot = document.getElementById("app");
const observeAppConfig = {
  subtree: true,
  childList: true,
};
const o = new MutationObserver(elementAddedCallback);
o.observe(appRoot, observeAppConfig);
console.log(o);
